import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { designationService, CreateDesignationPayload } from '../designationService';

/** All designations (used for dropdowns/selects). */
export function useDesignations() {
  return useQuery({
    queryKey: ['designations', 'all'],
    queryFn: () => designationService.getAllDesignations(),
    staleTime: 30000,
  });
}

/** Paginated + searchable designations (used by the list page). */
export function usePaginatedDesignations(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: ['designations', 'paginated', page, limit, search],
    queryFn: () => designationService.getDesignations(page, limit, search),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useActiveDesignations() {
  return useQuery({
    queryKey: ['designations', 'active'],
    queryFn: () => designationService.getActiveDesignations(),
    staleTime: 30000,
  });
}

export function useCreateDesignation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateDesignationPayload) => designationService.createDesignation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['designations'] });
    },
  });
}

export function useUpdateDesignation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateDesignationPayload> }) =>
      designationService.updateDesignation(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['designations'] });
    },
  });
}

export function useDeleteDesignation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => designationService.deleteDesignation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['designations'] });
    },
  });
}

/** Bulk-delete designations as a single silent mutation (loops the single-delete endpoint). */
export function useBulkDeleteDesignations() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        await designationService.deleteDesignation(id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['designations'] });
    },
  });
}
